import React from 'react';
import { Upload, MapPin, Plus, Trash2 } from 'lucide-react';
import { EditableText } from '../components/EditableText';
import { BuilderButton } from '../components/BuilderButton';
import { ImageDropZone } from '../components/ImageDropZone';

export interface LocationCard {
  image: string;
  title: string;
}

export interface FeatureLocationContent {
  locations: LocationCard[];
}

interface FeatureLocationSectionProps {
  content: FeatureLocationContent;
  onUpdate: (updates: Partial<FeatureLocationContent>) => void;
  organizationId: string;
  isPreview: boolean;
  onLocationImageUpload?: (index: number, file: File) => void;
  accentColor?: string;
}

export const FeatureLocationSection: React.FC<FeatureLocationSectionProps> = ({
  content,
  onUpdate,
  organizationId,
  isPreview,
  onLocationImageUpload,
  accentColor,
}) => {
  const handleLocationImageClick = (index: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          if (onLocationImageUpload) {
            onLocationImageUpload(index, file);
          }
        } else {
          alert('Please select an image file');
        }
      }
    };
    input.click();
  };

  const updateLocationTitle = (index: number, title: string) => {
    const updatedLocations = [...content.locations];
    updatedLocations[index] = { ...updatedLocations[index], title };
    onUpdate({ locations: updatedLocations });
  };

  const addLocation = () => {
    if (content.locations.length < 6) {
      const newLocation: LocationCard = { image: '', title: '[New Location]' };
      onUpdate({ locations: [...content.locations, newLocation] });
    }
  };

  const removeLocation = (index: number) => {
    if (content.locations.length > 1) {
      const updatedLocations = content.locations.filter((_, i) => i !== index);
      onUpdate({ locations: updatedLocations });
    }
  };

  // Determine grid columns based on number of locations
  const getGridClass = () => {
    const count = content.locations.length;
    if (count <= 3) return 'lg:grid-cols-3';
    if (count === 4) return 'lg:grid-cols-4';
    if (count === 5) return 'lg:grid-cols-5';
    return 'lg:grid-cols-6';
  };

  return (
    <div className="w-full bg-gray-100 py-16">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Header Tagline - Static */}
          <div className="mb-4">
            <div
              className="font-bold text-sm uppercase tracking-wider"
              style={{ color: accentColor || '#FACC15' }}
            >
              FEATURED LOCATIONS
            </div>
          </div>
          <div className="text-gray-900 text-5xl font-bold">Areas We Serve</div>
        </div>

        {/* Location Grid - Dynamic columns based on count */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${getGridClass()} gap-6 mb-8`}>
          {content.locations.map((location, index) => (
            <div key={index} className="relative group">
              {/* Delete Button */}
              {!isPreview && content.locations.length > 1 && (
                <button
                  onClick={() => removeLocation(index)}
                  className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors z-10"
                  title="Remove location"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              {/* Location Image */}
              {location.image ? (
                <div className="relative">
                  <img
                    src={location.image}
                    alt={location.title}
                    className="w-full h-80 object-cover rounded-lg shadow-lg"
                  />
                  {!isPreview && (
                    <div className="absolute inset-0">
                      <ImageDropZone
                        onImageDrop={(file) => {
                          if (onLocationImageUpload) {
                            onLocationImageUpload(index, file);
                          }
                        }}
                        isUploading={false}
                        label="Drop new location photo"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-80 bg-gradient-to-br from-gray-200 to-gray-100 rounded-lg relative">
                  {!isPreview && (
                    <ImageDropZone
                      onImageDrop={(file) => {
                        if (onLocationImageUpload) {
                          onLocationImageUpload(index, file);
                        }
                      }}
                      isUploading={false}
                      label="Drop location photo"
                      showAlways
                    />
                  )}
                </div>
              )}

              {/* Location Title - Centered below image */}
              <div className="mt-4 text-center">
                <div className="text-gray-900 text-xl font-bold">
                  <EditableText
                    value={location.title}
                    onChange={(value: string) => updateLocationTitle(index, value)}
                    isPreview={isPreview}
                    className="text-center"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Location Button */}
        {!isPreview && content.locations.length < 6 && (
          <div className="flex justify-center">
            <button
              onClick={addLocation}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Location ({content.locations.length}/6)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
